import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useTranslation } from 'react-i18next';

import { ColorMode } from '../../theme/buildTheme';
import {
  PILL_MIN_HEIGHT, PILL_RADIUS, pillBg, pillBgHover,
} from '../layout/headerPill';

interface ThemeToggleProps {
  mode: ColorMode;
  onToggle: () => void;
  /** When true, renders as a full-width list row (for use in mobile menu overlay). */
  menuMode?: boolean;
}

/** Header light/dark switch. Sits on the primary AppBar alongside the language picker. */
const ThemeToggle = ({ mode, onToggle, menuMode }: ThemeToggleProps) => {
  const { t } = useTranslation();
  const label = mode === 'dark' ? t('finder.switch_to_light') : t('finder.switch_to_dark');
  const Icon = mode === 'dark' ? LightModeIcon : DarkModeIcon;

  if (menuMode) {
    return (
      <ButtonBase
        onClick={onToggle}
        aria-label={label}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          px: 2,
          minHeight: 52,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Icon sx={{ fontSize: 20, opacity: 0.9 }} />
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          {label}
        </Typography>
      </ButtonBase>
    );
  }

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={onToggle}
        aria-label={label}
        size="small"
        sx={{
          color: 'text.primary',
          borderRadius: PILL_RADIUS,
          width: PILL_MIN_HEIGHT,
          height: PILL_MIN_HEIGHT,
          bgcolor: pillBg,
          '&:hover': { bgcolor: pillBgHover },
        }}
      >
        <Icon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;
